import React from 'react';
import { BonusStatusSummary } from '../../providers/types';
import { format } from 'date-fns';

interface BonusStatusCardProps {
  summary: BonusStatusSummary;
  isActive: boolean;
  onClick: () => void;
}

export const BonusStatusCard: React.FC<BonusStatusCardProps> = ({
  summary,
  isActive,
  onClick
}) => {
  // Determine milestones based on bonus type
  const getMilestones = () => {
    const milestones = [];
    
    // Stage5 Bonus: Only 4 milestones (DM, Ops, Payroll Processed, Payroll Paid)
    if (summary.bonusTypeId === 1) { // Stage5
      if (summary.dmReviewedOn) {
        milestones.push({
          label: 'DM Approved',
          date: summary.dmReviewedOn,
          person: summary.dmReviewer,
          completed: true
        });
      }
      
      if (summary.opsReviewedOn) {
        milestones.push({
          label: 'Ops Approved',
          date: summary.opsReviewedOn,
          person: summary.opsReviewer,
          completed: true
        });
      }
      
      if (summary.payrollProcessedOn) {
        milestones.push({
          label: 'Payroll Processed',
          date: summary.payrollProcessedOn,
          person: summary.payrollProcessor,
          completed: true
        });
      }
      
      if (summary.payrollPaidOn) {
        milestones.push({
          label: 'Payroll Paid',
          date: summary.payrollPaidOn,
          person: summary.payrollPaidBy,
          completed: true
        });
      }
    } else { // Closing Bonus: All 6 milestones
      if (summary.pmReviewedOn) {
        milestones.push({
          label: 'PM Approved',
          date: summary.pmReviewedOn,
          person: summary.pmReviewer,
          completed: true
        });
      }
      
      if (summary.spmReviewedOn) {
        milestones.push({
          label: 'SPM Approved',
          date: summary.spmReviewedOn,
          person: summary.spmReviewer,
          completed: true
        });
      }
      
      if (summary.dmReviewedOn) {
        milestones.push({
          label: 'DM Approved',
          date: summary.dmReviewedOn,
          person: summary.dmReviewer,
          completed: true
        });
      }
      
      if (summary.opsReviewedOn) {
        milestones.push({
          label: 'Ops Approved',
          date: summary.opsReviewedOn,
          person: summary.opsReviewer,
          completed: true
        });
      }
      
      if (summary.payrollProcessedOn) {
        milestones.push({
          label: 'Payroll Processed',
          date: summary.payrollProcessedOn,
          person: summary.payrollProcessor,
          completed: true
        });
      }
      
      if (summary.payrollPaidOn) {
        milestones.push({
          label: 'Payroll Paid',
          date: summary.payrollPaidOn,
          person: summary.payrollPaidBy,
          completed: true
        });
      }
    }
    
    // Sort by date (most recent first)
    return milestones.sort((a, b) => b.date.getTime() - a.date.getTime());
  };

  const milestones = getMilestones();
  const completedCount = milestones.length;
  const totalMilestones = summary.bonusTypeId === 1 ? 4 : 6; // Stage5 = 4, Closing = 6
  
  // Format status display name
  const formatStatusName = (status: string): string => {
    // Convert "InOpsReview" to "In Ops Review"
    return status
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .trim() // Remove leading space
      .replace(/^In /, 'In '); // Ensure "In" stays at the beginning
  };

  // Status badge styling
  const getStatusBadgeClass = (status: string): string => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('paid')) return 'status-paid';
    if (statusLower.includes('opsapproved') || statusLower.includes('approved')) return 'status-approved';
    if (statusLower.includes('inpayrollprocessing') || statusLower.includes('processed')) return 'status-processed';
    if (statusLower.includes('inopsreview') || statusLower.includes('pending') || statusLower.includes('pmreview')) return 'status-pending';
    if (statusLower.includes('draft') || statusLower.includes('notstarted')) return 'status-draft';
    if (statusLower.includes('busted')) return 'status-busted';
    return 'status-default';
  };

  return (
    <div 
      className={`bonus-status-card ${isActive ? 'active' : ''}`}
      onClick={onClick}
    >
      <div className="status-card-header">
        <h3>{summary.bonusTypeName} Bonus</h3>
        <span className={`status-badge ${getStatusBadgeClass(summary.currentStatus)}`}>
          {formatStatusName(summary.currentStatus)}
        </span>
      </div>

      {summary.lastUpdated && (
        <div className="status-card-meta">
          Last updated {format(summary.lastUpdated, 'MMM d, yyyy h:mm a')}
          {summary.lastUpdatedBy && <span> by {summary.lastUpdatedBy}</span>}
        </div>
      )}

      <div className="status-milestones">
        {milestones.length === 0 ? (
          <p className="no-milestones">No milestones completed yet</p>
        ) : (
          <>
            {/* <div className="milestone-progress">
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ width: `${(completedCount / totalMilestones) * 100}%` }}
                />
              </div>
              <span className="progress-text">
                {completedCount} of {totalMilestones} milestones completed
              </span>
            </div> */}
            
            <ul className="milestone-list">
              {milestones.map((milestone, index) => (
                <li key={index} className="milestone-item">
                  <svg className="milestone-check" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                  </svg>
                  <div className="milestone-content">
                    <span className="milestone-label">{milestone.label}</span>
                    <span className="milestone-meta">
                      {format(milestone.date, 'MMM d, yyyy h:mm a')}
                      {milestone.person && ` • ${milestone.person}`}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {summary.bim360UploadIssue && (
        <div className="status-alert warning">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
          </svg>
          <span>BIM360 Upload Issue</span>
        </div>
      )}

      {summary.bim360Upload && !summary.bim360UploadIssue && (
        <div className="status-alert success">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
          </svg>
          <span>Uploaded to BIM360 {summary.bim360UploadOn && `on ${format(summary.bim360UploadOn, 'MMM d, yyyy h:mm a')}`}</span>
        </div>
      )}
    </div>
  );
};